import { Generate } from '../generate/generate';
import { FromArray } from '../array/index';

// ─── 1-D (original, kept for backward compatibility) ────────────────────────

interface Cluster {
    id: number;
    position: number;
    childs: number[];
}

/**
 * Simple 1D K-means clustering.
 * Runs multiple attempts and returns the best result (most balanced cluster sizes).
 * @param k Number of clusters (default 2).
 * @param arr Input data points.
 * @param attempts Number of attempts to run (default 1).
 */
export const KMeans = (k: number = 2, arr: number[], attempts: number = 1): Cluster[] => {
    if (arr.length === 0) throw new Error("Empty array.");

    const max = Math.max(...arr);
    const min = Math.min(...arr);
    const variations: Cluster[][] = [];

    for (let attempt = 0; attempt < attempts; attempt++) {
        let clusters: Cluster[] = [];
        for (let i = 0; i < k; i++)
            clusters.push({ id: i + 1, position: Generate.int(min, max + 1), childs: [] });

        let previousClusters: Cluster[] = [];
        while (!samePositions(clusters, previousClusters)) {
            previousClusters = clusters.map((c) => ({ ...c }));
            for (const c of clusters) c.childs = [];
            clusters = assignPoints(clusters, arr);
            clusters = recalibrate(clusters);
        }
        variations.push(clusters);
    }

    if (variations.length === 1) return variations[0];

    // Pick the variation with the most balanced cluster sizes
    const scores = variations.map((v) => {
        let score = 0;
        for (let i = 0; i < v.length - 1; i++)
            score += Math.abs(v[i + 1].childs.length - v[i].childs.length);
        return score;
    });
    const bestIndex = FromArray.getSmallest(scores, 1, true)[0];
    return variations[bestIndex];
};

function samePositions(clusters: Cluster[], prev: Cluster[]): boolean {
    if (prev.length === 0) return false;
    return clusters.every((c, i) => c.position === prev[i].position);
}

function assignPoints(clusters: Cluster[], arr: number[]): Cluster[] {
    for (const point of arr) {
        const distances = clusters.map((c) => Math.abs(c.position - point));
        const nearest = FromArray.getSmallest(distances, 1, true)[0];
        clusters[nearest].childs.push(point);
    }
    return clusters;
}

function recalibrate(clusters: Cluster[]): Cluster[] {
    for (const c of clusters) {
        if (c.childs.length === 0) continue;
        const mean = c.childs.reduce((a, b) => a + b, 0) / c.childs.length;
        c.position = Number(mean.toFixed(2));
    }
    return clusters;
}

// ─── N-dimensional ──────────────────────────────────────────────────────────

export interface NDCluster<T> {
    /** 1-based cluster id */
    id: number;
    /** Mean position in feature space */
    centroid: number[];
    /** Original data items assigned to this cluster */
    members: T[];
    /** Number of members */
    size: number;
    /**
     * Within-cluster sum of squares — sum of squared Euclidean distances
     * from each member to the centroid. Lower means a tighter, more
     * cohesive cluster.
     */
    wcss: number;
}

export type FeatureExtractor<T> = (item: T) => number[];

export interface KMeansNDOptions {
    /**
     * Number of independent runs. The run with the lowest total WCSS wins.
     * More attempts → more reliable result at the cost of compute.
     * Default: 5.
     */
    attempts?: number;
    /**
     * Centroid initialisation strategy.
     * - `'kmeans++'` (default) spreads initial centroids far apart,
     *   which dramatically reduces the chance of a poor local minimum.
     * - `'random'` picks k random data points as starting centroids.
     */
    init?: 'random' | 'kmeans++';
    /**
     * Hard cap on iteration count per attempt.
     * Default: 300.
     */
    maxIter?: number;
}

/**
 * N-dimensional K-means clustering suitable for production analytics
 * (customer segmentation, product segmentation, etc.).
 *
 * @param k Number of clusters.
 * @param data Array of items to cluster.
 * @param features Function that extracts a numeric feature vector from each item.
 *   All vectors must have the same length.
 * @param options Optional tuning parameters.
 *
 * @example
 * // Customer segmentation by recency, frequency, monetary value (RFM)
 * const segments = KMeansND(3, customers, (c) => [c.recency, c.frequency, c.spend]);
 *
 * @example
 * // Works with plain number arrays too
 * const result = KMeansND(2, [[1,2],[3,4],[100,200]], (x) => x);
 */
export const KMeansND = <T>(
    k: number,
    data: T[],
    features: FeatureExtractor<T>,
    options: KMeansNDOptions = {},
): NDCluster<T>[] => {
    if (data.length === 0) throw new Error("Empty data array.");
    if (k < 1) throw new Error("k must be >= 1.");
    if (k > data.length) throw new Error("k cannot exceed the number of data points.");

    const { attempts = 5, init = 'kmeans++', maxIter = 300 } = options;

    const points = data.map(features);
    const dims = points[0].length;
    if (points.some((p) => p.length !== dims))
        throw new Error("All feature vectors must have the same length.");

    let bestClusters: NDCluster<T>[] | null = null;
    let bestTotalWCSS = Infinity;

    for (let attempt = 0; attempt < attempts; attempt++) {
        const centroids =
            init === 'kmeans++'
                ? initKMeansPlusPlus(points, k)
                : initRandom(points, k);

        const memberIndices: number[][] = Array.from({ length: k }, () => []);
        let prevCentroids: number[][] = [];
        let iter = 0;

        while (!centroidsConverged(centroids, prevCentroids) && iter < maxIter) {
            prevCentroids = centroids.map((c) => [...c]);
            for (const m of memberIndices) m.length = 0;

            // Assign each point to the nearest centroid
            for (let p = 0; p < points.length; p++) {
                const dists = centroids.map((c) => euclidean(c, points[p]));
                let nearest = 0;
                for (let c = 1; c < k; c++)
                    if (dists[c] < dists[nearest]) nearest = c;
                memberIndices[nearest].push(p);
            }

            // Re-initialise empty clusters to a random data point
            for (let c = 0; c < k; c++) {
                if (memberIndices[c].length === 0) {
                    const randomIdx = Math.floor(Math.random() * points.length);
                    memberIndices[c].push(randomIdx);
                }
            }

            // Recompute centroids as mean of assigned points
            for (let c = 0; c < k; c++) {
                const newCentroid = new Array(dims).fill(0);
                for (const idx of memberIndices[c])
                    for (let d = 0; d < dims; d++)
                        newCentroid[d] += points[idx][d];
                for (let d = 0; d < dims; d++)
                    newCentroid[d] /= memberIndices[c].length;
                centroids[c] = newCentroid;
            }

            iter++;
        }

        // Build result clusters and compute WCSS
        const clusters: NDCluster<T>[] = centroids.map((centroid, c) => {
            const members = memberIndices[c].map((i) => data[i]);
            const wcss = memberIndices[c].reduce(
                (sum, i) => sum + euclideanSq(points[i], centroid),
                0,
            );
            return { id: c + 1, centroid, members, size: members.length, wcss };
        });

        const totalWCSS = clusters.reduce((s, c) => s + c.wcss, 0);
        if (totalWCSS < bestTotalWCSS) {
            bestTotalWCSS = totalWCSS;
            bestClusters = clusters;
        }
    }

    return bestClusters!;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function euclidean(a: number[], b: number[]): number {
    return Math.sqrt(euclideanSq(a, b));
}

function euclideanSq(a: number[], b: number[]): number {
    let sum = 0;
    for (let i = 0; i < a.length; i++) sum += (a[i] - b[i]) ** 2;
    return sum;
}

/** K-means++ centroid seeding for better convergence. */
function initKMeansPlusPlus(points: number[][], k: number): number[][] {
    const centroids: number[][] = [];
    // Pick first centroid at random
    centroids.push([...points[Math.floor(Math.random() * points.length)]]);

    for (let c = 1; c < k; c++) {
        // For each point, D² = squared distance to nearest existing centroid
        const weights = points.map((p) => {
            let minDist = Infinity;
            for (const centroid of centroids) {
                const d = euclideanSq(p, centroid);
                if (d < minDist) minDist = d;
            }
            return minDist;
        });
        // Weighted random selection
        centroids.push([...weightedChoice(points, weights)]);
    }
    return centroids;
}

function initRandom(points: number[][], k: number): number[][] {
    const indices = new Set<number>();
    while (indices.size < k)
        indices.add(Math.floor(Math.random() * points.length));
    return [...indices].map((i) => [...points[i]]);
}

function weightedChoice(points: number[][], weights: number[]): number[] {
    const total = weights.reduce((a, b) => a + b, 0);
    let r = Math.random() * total;
    for (let i = 0; i < weights.length; i++) {
        r -= weights[i];
        if (r <= 0) return points[i];
    }
    return points[points.length - 1];
}

const CONVERGENCE_EPSILON = 1e-10;

function centroidsConverged(curr: number[][], prev: number[][]): boolean {
    if (prev.length === 0) return false;
    return curr.every((c, i) => euclidean(c, prev[i]) < CONVERGENCE_EPSILON);
}
